import { Alert, AlertTitle, Box, Typography } from "@mui/material";
import { useGetMany, useRecordContext } from "react-admin";
import { DocumentRead, DocumentStatus, ResourceName, TargetSchema, type User } from "@/types";
import { DATE_FORMATS, formatDate } from "@/utils/formatters/dateFormatter";
import { useDocumentLoads } from "./result-tab/hooks";
import { usesLoadResultView } from "../utils/documentShowLayout";

const LOADED_STATUSES = new Set<string>([DocumentStatus.LOADED, DocumentStatus.COMPLETED]);

/**
 * Post-load rail (#7440 AC7, artboard 1a loaded state): when the file loaded,
 * what loaded, each check the reviewer acknowledged and by whom, and that undo
 * is an admin delete. Sits beside DocumentRejectedBanner on DocumentShow and
 * self-hides for every other status.
 *
 * The acknowledgements come from the document read model (server-stamped on
 * accept). The claim-line count comes from the latest load's loaded_records;
 * the load does not record a dollar total, so the "$X paid" the design shows
 * is not available here yet (follow-up noted on #7440).
 */
export const DocumentLoadedBanner = () => {
  const documentRecord = useRecordContext<DocumentRead>();
  const status = documentRecord?.status ? String(documentRecord.status) : "";
  // Claims documents carry this in the Result tab's context bar and the
  // drawer's Review record group (#7550 AC8/AC9); the band is theirs no more.
  const show = LOADED_STATUSES.has(status) && !usesLoadResultView(documentRecord);
  const acks = show ? (documentRecord?.review_acknowledgements ?? []) : [];
  const userIds = Array.from(new Set(acks.map((a) => a.user_id).filter((u): u is string => !!u)));
  const { data: users } = useGetMany<User>(ResourceName.USERS, { ids: userIds }, { enabled: userIds.length > 0 });
  const { latestLoad } = useDocumentLoads(show ? documentRecord?.id : undefined);

  if (!show || !documentRecord) return null;

  const when = documentRecord.processed_datetime
    ? formatDate(String(documentRecord.processed_datetime), DATE_FORMATS.DISPLAY_LONG_WITH_TIME)
    : documentRecord.modified_datetime
      ? formatDate(String(documentRecord.modified_datetime), DATE_FORMATS.DISPLAY_LONG_WITH_TIME)
      : null;
  const lines = latestLoad?.loaded_records?.filter((r) => r.target_schema === TargetSchema.CLAIM_ITEM).length ?? null;
  const nameFor = (userId?: string): string => {
    const u = userId ? users?.find((x) => String(x.id) === userId) : undefined;
    return u ? u.full_name || u.preferred_name || u.email : "a reviewer";
  };

  return (
    <Box px={5} pb={1}>
      <Alert severity="success" sx={{ alignItems: "flex-start" }} data-testid="document-loaded-banner">
        <AlertTitle>{when ? `Loaded ${when}` : "Loaded"}</AlertTitle>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          {lines !== null && (
            <Typography variant="body2">
              {lines.toLocaleString()} claim {lines === 1 ? "line" : "lines"} loaded
            </Typography>
          )}
          {acks.map((a, i) => (
            <Typography key={`${a.check}-${i}`} variant="body2">
              {a.detail || a.check} · confirmed by {nameFor(a.user_id)}
              {a.at ? ` on ${formatDate(String(a.at), DATE_FORMATS.DISPLAY_LONG)}` : ""}
            </Typography>
          ))}
          <Typography variant="body2" color="text.secondary">
            Undo requires an admin to delete this document.
          </Typography>
        </Box>
      </Alert>
    </Box>
  );
};
